import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, fmtMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText, FileCode, Trash2, Pencil, X, FileArchive,
  Loader2, Plus,
} from "lucide-react";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatXml(xml) {
  const PADDING = "  ";
  const reg = /(>)(<)(\/*)/g;
  let pad = 0;
  let out = "";
  xml.replace(reg, "$1\n$2$3").split("\n").forEach((node) => {
    let indent = 0;
    if (node.match(/^<\/\w/)) { if (pad > 0) pad -= 1; }
    else if (node.match(/^<\w[^>]*[^/]>.*$/) && !node.match(/<\/\w/)) indent = 1;
    out += PADDING.repeat(pad) + node + "\n";
    pad += indent;
  });
  return out.trim();
}

export default function Inspectores() {
  const { can } = useAuth();
  const canEdit = can("inspectores", "edit");
  const canDelete = can("inspectores", "delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const [mes, setMes] = useState("");
  const [anio, setAnio] = useState(String(now.getFullYear()));

  // Crear
  const xmlRef = useRef();
  const pdfRef = useRef();
  const [newXml, setNewXml] = useState(null);
  const [newPdf, setNewPdf] = useState(null);
  const [creating, setCreating] = useState(false);

  // Selección / export
  const [selected, setSelected] = useState(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState("both");
  const [exporting, setExporting] = useState(false);

  // Visores
  const [pdfView, setPdfView] = useState(null);
  const [xmlView, setXmlView] = useState(null);

  // Edición
  const [editing, setEditing] = useState(null);
  const editXmlRef = useRef();
  const editPdfRef = useRef();
  const [editXml, setEditXml] = useState(null);
  const [editPdf, setEditPdf] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const token = localStorage.getItem("token");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (mes) params.append("mes", mes);
    if (anio) params.append("anio", anio);
    api.get(`/inspectores/facturas?${params.toString()}`)
      .then((r) => { setRows(r.data); setSelected(new Set()); })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mes, anio]);

  const createFactura = async () => {
    if (!newXml || !newPdf) {
      toast.error("Debes adjuntar el XML y el PDF");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("xml_file", newXml);
      fd.append("pdf_file", newPdf);
      await api.post("/inspectores/facturas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura registrada");
      setNewXml(null); setNewPdf(null);
      if (xmlRef.current) xmlRef.current.value = "";
      if (pdfRef.current) pdfRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta factura?")) return;
    try {
      await api.delete(`/inspectores/facturas/${id}`);
      toast.success("Factura eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    if (!editXml && !editPdf) {
      toast.error("Selecciona al menos un archivo para reemplazar");
      return;
    }
    setSavingEdit(true);
    try {
      const fd = new FormData();
      if (editXml) fd.append("xml_file", editXml);
      if (editPdf) fd.append("pdf_file", editPdf);
      await api.put(`/inspectores/facturas/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura actualizada");
      setEditing(null); setEditXml(null); setEditPdf(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.post("/inspectores/export-zip",
        { ids: Array.from(selected), mode: exportMode },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "facturas.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selected.size} factura(s) exportada(s)`);
      setExportOpen(false);
    } catch (err) {
      toast.error("No se pudo generar el ZIP");
    } finally {
      setExporting(false);
    }
  };

  const openXml = async (r) => {
    setXmlView({ id: r.id, content: "Cargando…" });
    try {
      const res = await api.get(`/inspectores/file/${r.id}/xml`, { responseType: "text" });
      setXmlView({ id: r.id, content: formatXml(typeof res.data === "string" ? res.data : String(res.data)) });
    } catch (err) {
      setXmlView({ id: r.id, content: "No se pudo cargar el XML" });
    }
  };

  const pdfUrl = useMemo(() => (id) => `${API}/inspectores/file/${id}/pdf?auth=${token}`, [token]);

  const COLS = ["Folio", "Fecha", "Folio Fiscal (UUID)", "Emisor", "Receptor", "Descripción",
    "Subtotal", "Descuento", "IVA", "Total", "PDF", "XML"];
  const colCount = 1 + COLS.length + (canEdit || canDelete ? 1 : 0);
  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-6">
      {/* Crear factura */}
      {canEdit && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Registrar factura</h3>
            <p className="text-sm text-slate-500">Adjunta el XML (CFDI) y el PDF. Ambos son obligatorios. Los datos se extraen del XML.</p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-xs text-slate-500 flex items-center gap-1"><FileCode className="h-3 w-3 text-blue-600" /> Archivo XML</label>
              <input ref={xmlRef} data-testid="new-xml-input" type="file" accept=".xml,text/xml,application/xml" className={input}
                onChange={(e) => setNewXml(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 flex items-center gap-1"><FileText className="h-3 w-3 text-red-600" /> Archivo PDF</label>
              <input ref={pdfRef} data-testid="new-pdf-input" type="file" accept=".pdf,application/pdf" className={input}
                onChange={(e) => setNewPdf(e.target.files?.[0] || null)} />
            </div>
            <button data-testid="create-factura-btn" onClick={createFactura} disabled={creating || !newXml || !newPdf}
              className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Agregar factura
            </button>
          </div>
        </div>
      )}

      {/* Listado */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold text-slate-900">Facturas</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="filtro-mes" value={mes} onChange={(e) => setMes(e.target.value)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="">Todos los meses</option>
              {MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
            </select>
            <input data-testid="filtro-anio" type="number" placeholder="Año" value={anio} onChange={(e) => setAnio(e.target.value)} className="w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            {canEdit && (
              <button data-testid="export-zip-btn" onClick={() => setExportOpen(true)} disabled={selected.size === 0}
                className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
                <FileArchive className="h-4 w-4" /> Exportar ZIP ({selected.size})
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input data-testid="select-all" type="checkbox" checked={allChecked} onChange={toggleAll} className="rounded border-slate-300" />
                </th>
                {COLS.map((h) => (
                  <th key={h} className={`px-4 py-3 font-medium ${["Subtotal", "Descuento", "IVA", "Total"].includes(h) ? "text-right" : ["PDF", "XML"].includes(h) ? "text-center" : "text-left"}`}>{h}</th>
                ))}
                {(canEdit || canDelete) && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody data-testid="facturas-table">
              {loading && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Cargando…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400">Sin facturas para el periodo seleccionado</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <input data-testid={`select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-slate-300" />
                  </td>
                  <td className="px-4 py-3 text-slate-900">{r.folio || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.fecha ? String(r.fecha).slice(0, 10) : "—"}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{r.uuid || "—"}</td>
                  <td className="px-4 py-3 text-slate-900 max-w-[200px] truncate" title={r.emisor_nombre}>{r.emisor_nombre || "—"}<span className="block text-xs text-slate-400">{r.emisor_rfc}</span></td>
                  <td className="px-4 py-3 text-slate-900 max-w-[200px] truncate" title={r.receptor_nombre}>{r.receptor_nombre || "—"}<span className="block text-xs text-slate-400">{r.receptor_rfc}</span></td>
                  <td className="px-4 py-3 text-slate-600 max-w-[240px] truncate" title={r.descripcion}>{r.descripcion || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(r.subtotal, "MXN")}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(r.descuento, "MXN")}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMoney(r.iva, "MXN")}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtMoney(r.total, "MXN")}</td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`view-pdf-${r.id}`} onClick={() => setPdfView(r)} className="inline-flex items-center justify-center text-red-500 hover:text-red-700" title="Ver PDF"><FileText className="h-4 w-4" /></button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button data-testid={`view-xml-${r.id}`} onClick={() => openXml(r)} className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700" title="Ver XML"><FileCode className="h-4 w-4" /></button>
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canEdit && <button data-testid={`edit-factura-${r.id}`} onClick={() => { setEditing(r); setEditXml(null); setEditPdf(null); }} className="text-slate-400 hover:text-slate-900 mr-3" title="Editar / reemplazar archivos"><Pencil className="h-4 w-4" /></button>}
                      {canDelete && <button data-testid={`del-factura-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 className="h-4 w-4" /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visor PDF */}
      {pdfView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setPdfView(null)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pdf-viewer">
            <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4 text-red-600" /> {pdfView.folio || "Factura"} · PDF</h3>
              <button onClick={() => setPdfView(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="pdf" src={pdfUrl(pdfView.id)} className="flex-1 w-full rounded-b-md" />
          </div>
        </div>
      )}

      {/* Visor XML */}
      {xmlView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setXmlView(null)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-3xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="xml-viewer">
            <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FileCode className="h-4 w-4 text-blue-600" /> Contenido XML</h3>
              <button onClick={() => setXmlView(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-slate-700 bg-slate-50 rounded-b-md">{xmlView.content}</pre>
          </div>
        </div>
      )}

      {/* Diálogo de exportación */}
      {exportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setExportOpen(false)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="export-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Exportar {selected.size} factura(s)</h3>
              <button onClick={() => setExportOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              {[["both", "Exportar XML y PDF"], ["xml", "Exportar únicamente XML"], ["pdf", "Exportar únicamente PDF"]].map(([v, l]) => (
                <label key={v} className={`flex items-center gap-3 border rounded-md px-4 py-3 cursor-pointer ${exportMode === v ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
                  <input data-testid={`export-mode-${v}`} type="radio" name="exportMode" value={v} checked={exportMode === v} onChange={() => setExportMode(v)} />
                  <span className="text-sm text-slate-800">{l}</span>
                </label>
              ))}
              <button data-testid="export-confirm" onClick={doExport} disabled={exporting}
                className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />} Generar ZIP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edición: reemplazar archivos */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="edit-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Editar factura {editing.folio}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500">Reemplaza el XML y/o el PDF. Al subir un nuevo XML se vuelven a extraer los datos fiscales.</p>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1"><FileCode className="h-3 w-3 text-blue-600" /> Nuevo XML (opcional)</label>
                <input ref={editXmlRef} data-testid="edit-xml-input" type="file" accept=".xml,text/xml,application/xml" className={input} onChange={(e) => setEditXml(e.target.files?.[0] || null)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1"><FileText className="h-3 w-3 text-red-600" /> Nuevo PDF (opcional)</label>
                <input ref={editPdfRef} data-testid="edit-pdf-input" type="file" accept=".pdf,application/pdf" className={input} onChange={(e) => setEditPdf(e.target.files?.[0] || null)} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                <button data-testid="edit-save" onClick={saveEdit} disabled={savingEdit} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50">
                  {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
